import type { BannerCta } from '../../../../services/ui';
import type { CoinType } from '../../../../stores/cointype/CoinTypeStore';

// Hand-synced with CTA_METAGAME_KEYS in admin-frontend (src/pages/BannerManagement/lobby/constants.ts) and
// admin-backend (src/services/lobbyBanner/constants.ts).
const CTA_METAGAME_KEYS = ['hammers', 'trail', 'puzzle', 'prizeDrop', 'cardWar'] as const;
type MetagameKey = (typeof CTA_METAGAME_KEYS)[number];

const isMetagameKey = (value: string): value is MetagameKey => (CTA_METAGAME_KEYS as readonly string[]).includes(value);

export type BannerCtaDeps = {
  startGame: (gameId: number, coin?: CoinType) => void;
  inviteFriendsEnabled: boolean;
  openInviteFriends: () => void;
  tournamentOfChampionsAvailable: boolean;
  getTournamentCanOpenDialog: () => boolean;
  fetchTournament: () => Promise<void>;
  openTournamentDialog: () => void;
  chaseAvailable: boolean;
  openChase: () => void;
  tournamentLeaderboardAvailable: boolean;
  getTournamentLeaderboardAvailable: () => boolean;
  crownSportsFeatureEnabled: boolean;
  sportEventAvailable: boolean;
  startSportEvent: () => void;
  crownJackpotAvailable: boolean;
  openCrownJackpot: () => void;
  benefitEventAvailable: (benefitKey: string) => boolean;
  openBenefitEvent: (benefitKey: string) => void;
  metagameAvailable: Record<MetagameKey, boolean>;
  startMetagame: Record<MetagameKey, () => void>;
};

const GAME_ID_PATTERN = /^[1-9][0-9]*$/;

// Hand-synced with admin-frontend's RivePreview click guard.
export const CTA_CLICK_COOLDOWN_MS = 1000;

export function guardRepeatClicks(
  action: () => void,
  cooldownMs = CTA_CLICK_COOLDOWN_MS,
  now: () => number = () => performance.now(),
) {
  let lastAcceptedAt = -Infinity;
  return () => {
    const timestamp = now();
    if (timestamp - lastAcceptedAt < cooldownMs) return;
    lastAcceptedAt = timestamp;
    action();
  };
}

const isValidGameId = (gameId: number) => Number.isSafeInteger(gameId) && gameId > 0;

export function resolveBannerCtaAction(
  cta: BannerCta | null,
  currentCoin: CoinType,
  deps: BannerCtaDeps,
): (() => void) | null {
  if (!cta) return null;

  switch (cta.type) {
    case 'GAME': {
      // The server already picked primary vs fallback game and the coin per current coin — don't re-decide here.
      if (cta.targets) {
        const target = cta.targets[currentCoin];
        if (!target || !isValidGameId(target.gameId)) return null;
        const { gameId, coin } = target;
        return () => deps.startGame(gameId, coin);
      }
      if (!GAME_ID_PATTERN.test(cta.value)) return null;
      const gameId = Number(cta.value);
      if (!Number.isSafeInteger(gameId)) return null;
      return () => deps.startGame(gameId);
    }
    case 'POPUP':
      switch (cta.value) {
        case 'INVITE_FRIEND':
          return deps.inviteFriendsEnabled ? deps.openInviteFriends : null;
        case 'CHAMPIONS_TOURNAMENT':
          if (!deps.tournamentOfChampionsAvailable || currentCoin !== 'SC') return null;
          return () => {
            void (async () => {
              await deps.fetchTournament();
              if (!deps.getTournamentCanOpenDialog()) return;
              deps.openTournamentDialog();
            })();
          };
        case 'CHASE':
          return deps.chaseAvailable ? deps.openChase : null;
        case 'TOURNAMENT_LEADERBOARD':
          if (!deps.tournamentLeaderboardAvailable) return null;
          return () => {
            void (async () => {
              await deps.fetchTournament();
              if (!deps.getTournamentLeaderboardAvailable()) return;
              deps.openTournamentDialog();
            })();
          };
        case 'CROWN_SPORTS':
          return deps.crownSportsFeatureEnabled && deps.sportEventAvailable ? deps.startSportEvent : null;
        case 'CROWN_JACKPOT':
          return deps.crownJackpotAvailable ? deps.openCrownJackpot : null;
        default:
          return null;
      }
    case 'METAGAME':
      return isMetagameKey(cta.value) && deps.metagameAvailable[cta.value] ? deps.startMetagame[cta.value] : null;
    case 'BENEFIT': {
      if (!cta.value) return null;
      const key = cta.value;
      return deps.benefitEventAvailable(key) ? () => deps.openBenefitEvent(key) : null;
    }
    default:
      return null;
  }
}
